import { useRef, useState, useCallback } from "react";
import {
    CheckboxOption,
    Mono,
    RadioGroup,
    RadioGroupOption,
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
    AutoGrowTextarea,
    cn,
    IconBadge,
} from "@athena/ui";
import { Check, PencilLine, Plus, Trash, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { QuestionType } from "@athena/common/graphql/generated/schema";

type LocalQuestionType = "single" | "true-false" | "multi" | "short" | "long";

/**
 * Server type to the authoring control's own vocabulary.
 *
 * `true-false` has no server type of its own — it is a `SINGLE_SELECT` carrying exactly the two
 * options True and False, which is what it always was underneath. The backend dropped the separate
 * `TRUE_FALSE` type and its `correctAnswer` boolean, so the right answer now lives in
 * `options[].isCorrect` like every other select question.
 */
const QUESTION_TYPE_MAP: Record<QuestionType, LocalQuestionType> = {
    SINGLE_SELECT: "single",
    MULTIPLE_SELECT: "multi",
    SHORT_ANSWER: "short",
    LONG_ANSWER: "long",
};

/** Every local type resolves to a real server type; the preset resolves to the same one as `single`. */
const SERVER_TYPE_MAP: Record<LocalQuestionType, QuestionType> = {
    single: "SINGLE_SELECT",
    "true-false": "SINGLE_SELECT",
    multi: "MULTIPLE_SELECT",
    short: "SHORT_ANSWER",
    long: "LONG_ANSWER",
};

/**
 * Whether a stored question should be shown as the True/False preset rather than a plain single
 * select — inferred from its options, since the distinction no longer exists server-side.
 */
const looksLikeTrueFalse = (options: readonly { text: string }[] | null | undefined): boolean => {
    if (!options || options.length !== 2) return false;
    const texts = options.map((o) => o.text.trim().toLowerCase()).sort();
    return texts[0] === "false" && texts[1] === "true";
};

const QUESTION_TYPES = [
    { value: "single", label: "Single select" },
    { value: "true-false", label: "True / False" },
    { value: "multi", label: "Multiple select" },
    { value: "short", label: "Short answer" },
    { value: "long", label: "Long answer" },
];

const TRUE_FALSE_OPTIONS = [
    "True",
    "False",
];

interface SelectOption {
    id: string;
    text: string;
}

const DEFAULT_OPTIONS: SelectOption[] = [
    { id: "1", text: "" },
    { id: "2", text: "" },
];

/** A fresh pair of empty options, so each reset gets its own array rather than a shared one. */
const blankOptions = (): SelectOption[] => DEFAULT_OPTIONS.map((option) => ({ ...option }));

const trueFalseOptions = (): SelectOption[] => TRUE_FALSE_OPTIONS.map((text, i) => ({ id: String(i + 1), text }));

interface ServerOption {
    id: string;
    text: string;
    isCorrect: boolean;
}

type UpdateInput = {
    questionType?: QuestionType | null;
    questionText?: string | null;
    options?: Array<{ text: string; isCorrect: boolean }> | null;
};

interface QuizQuestionProps {
    index: number;
    questionId: string;
    questionType?: QuestionType;
    questionText?: string;
    options?: ServerOption[];
    onUpdate: (input: UpdateInput) => void;
    onDelete: () => void;
}

/** The option editor's look: the row draws the border and background, the field just holds text. */
const OPTION_FIELD_CLASS =
    "text-foreground placeholder:text-muted-foreground min-h-0 flex-1 rounded-none border-none bg-transparent px-0 py-0 text-sm font-medium focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0";

export function QuizQuestion({
    index,
    questionType,
    questionText,
    options: serverOptions,
    onUpdate,
    onDelete,
}: QuizQuestionProps) {
    const { t } = useTranslation();
    const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [
        type,
        setType,
    ] = useState<LocalQuestionType>(() => {
        if (!questionType) return "true-false";
        const local = QUESTION_TYPE_MAP[questionType];
        // A single select holding only True and False is the preset, not a general single select.
        return local === "single" && looksLikeTrueFalse(serverOptions) ? "true-false" : local;
    });

    const [
        localText,
        setLocalText,
    ] = useState(questionText ?? "");

    const scheduleUpdate = useCallback(
        (input: UpdateInput) => {
            if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
            debounceTimeout.current = setTimeout(() => onUpdate(input), 800);
        },
        [onUpdate],
    );

    const initialAnswer = (() => {
        // No special case for true/false any more: its correct answer is an option like any other.
        if (serverOptions) {
            const correct = serverOptions.find((o) => o.isCorrect);
            return correct?.id ?? "";
        }
        return "";
    })();

    const [
        answer,
        setAnswer,
    ] = useState<string>(initialAnswer);
    const [
        options,
        setOptions,
    ] = useState<SelectOption[]>(
        serverOptions && serverOptions.length > 0
            ? serverOptions.map((o) => ({ id: o.id, text: o.text }))
            : type === "true-false"
              ? trueFalseOptions()
              : DEFAULT_OPTIONS,
    );
    const nextId = useRef(3);

    function addOption() {
        if (options.length >= 5) return;
        setOptions((prev) => [
            ...prev,
            { id: String(nextId.current++), text: "" },
        ]);
    }

    const [
        checkedAnswers,
        setCheckedAnswers,
    ] = useState<string[]>(serverOptions ? serverOptions.filter((o) => o.isCorrect).map((o) => o.id) : []);

    function toggleAnswer(id: string, checked: boolean) {
        setCheckedAnswers((prev) => {
            const next = checked
                ? [
                      ...prev,
                      id,
                  ]
                : prev.filter((a) => a !== id);
            scheduleUpdate({ options: options.map((o) => ({ text: o.text, isCorrect: next.includes(o.id) })) });
            return next;
        });
    }

    function removeOption(id: string) {
        if (options.length <= 2) return;
        const next = options.filter((o) => o.id !== id);
        setOptions(next);
        if (answer === id) setAnswer("");
        setCheckedAnswers((prev) => prev.filter((a) => a !== id));
        scheduleUpdate({ options: next.map((o) => ({ text: o.text, isCorrect: checkedAnswers.includes(o.id) })) });
    }

    function updateOption(id: string, text: string) {
        setOptions((prev) => {
            const next = prev.map((o) => (o.id === id ? { ...o, text } : o));
            scheduleUpdate({
                options: next.map((o) => ({
                    text: o.text,
                    isCorrect: checkedAnswers.includes(o.id) || answer === o.id,
                })),
            });
            return next;
        });
    }

    return (
        <div className="bg-background border-border flex flex-col rounded-lg border shadow-sm">
            {/* Header */}
            <div className="bg-muted/40 border-border flex items-center justify-between gap-3 border-b px-4 py-3">
                <div className="flex items-center gap-3">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-indigo-600/10">
                        <Mono className="text-sm font-semibold text-indigo-600">{index}</Mono>
                    </div>
                    <Select
                        value={type}
                        onValueChange={(v) => {
                            const local = v as LocalQuestionType;
                            setType(local);

                            /**
                             * Choosing the preset writes the two options along with the type — the
                             * server has no idea this is a true/false question, so the options are
                             * what make it one. Held in local state as well, so leaving the preset
                             * again can tell what the options currently are.
                             */
                            if (local === "true-false") {
                                setOptions(trueFalseOptions());
                                setAnswer("");
                                setCheckedAnswers([]);
                                onUpdate({
                                    questionType: "SINGLE_SELECT",
                                    options: TRUE_FALSE_OPTIONS.map((text) => ({ text, isCorrect: false })),
                                });
                                return;
                            }

                            /**
                             * Leaving the preset for a real select empties the options.
                             *
                             * Every question starts as True/False, so a plain single select
                             * inherited True and False as its two answers and the author had to
                             * clear both before writing their own. Blank options show the
                             * `Option 1` / `Option 2` placeholders instead.
                             *
                             * Only when the options still *are* the preset. Anything else is text
                             * somebody wrote, and switching between single and multiple select is
                             * a routine thing to do to a question that is already written — that
                             * must not throw the answers away.
                             */
                            const isSelect = local === "single" || local === "multi";
                            if (isSelect && looksLikeTrueFalse(options)) {
                                const cleared = blankOptions();
                                setOptions(cleared);
                                setAnswer("");
                                setCheckedAnswers([]);
                                onUpdate({
                                    questionType: SERVER_TYPE_MAP[local],
                                    options: cleared.map((option) => ({ text: option.text, isCorrect: false })),
                                });
                                return;
                            }

                            onUpdate({ questionType: SERVER_TYPE_MAP[local] });
                        }}
                    >
                        <SelectTrigger className="h-8 w-44 text-xs">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-background border-border border shadow-md">
                            <SelectGroup>
                                {QUESTION_TYPES.map((qt) => (
                                    <SelectItem key={qt.value} value={qt.value} className="text-xs">
                                        {qt.label}
                                    </SelectItem>
                                ))}
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex items-center gap-3">
                    {(type === "short" || type === "long") && (
                        <IconBadge variant="default" icon={<PencilLine className="h-3 w-3" />}>
                            {t("admin.lms.ungraded")}
                        </IconBadge>
                    )}
                    <button type="button" onClick={onDelete} aria-label="Delete question">
                        <Trash className="text-muted-foreground hover:text-destructive h-4 w-4 transition-colors" />
                    </button>
                </div>
            </div>

            {/* Question body */}
            <div className="flex flex-col gap-3 p-4">
                <AutoGrowTextarea
                    className="text-foreground placeholder:text-muted-foreground min-h-0 w-full rounded-none border-none bg-transparent px-0 py-0 text-sm focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
                    placeholder={t("admin.lms.typeYourQuestionPlaceholder")}
                    value={localText}
                    onChange={(e) => {
                        setLocalText(e.target.value);
                        scheduleUpdate({ questionText: e.target.value });
                    }}
                />

                {type === "true-false" && (
                    <div className="flex flex-col gap-2">
                        <span className="text-muted-foreground text-xs">{t("admin.lms.clickCorrectAnswer")}</span>
                        <RadioGroup
                            value={answer}
                            onValueChange={(v) => {
                                setAnswer(v);
                                /**
                                 * Written as options, not as a boolean. The server has no true/false
                                 * type — the correct answer is the option flagged `isCorrect`, exactly
                                 * as it is for a single select. So the radios are valued by option id
                                 * too: a saved answer comes back as one, and a reopened question has
                                 * to be able to show it.
                                 */
                                onUpdate({
                                    options: options.map((o) => ({ text: o.text, isCorrect: o.id === v })),
                                });
                            }}
                            className="flex flex-col gap-2"
                        >
                            {options.map((option) => (
                                <RadioGroupOption key={option.id} value={option.id} label={option.text} />
                            ))}
                        </RadioGroup>
                    </div>
                )}

                {type === "multi" && (
                    <div className="flex flex-col gap-2">
                        <span className="text-muted-foreground text-xs">{t("admin.lms.clickCorrectAnswers")}</span>
                        <div className="flex flex-col gap-2">
                            {options.map((opt, i) => {
                                const checked = checkedAnswers.includes(opt.id);
                                return (
                                    <div
                                        key={opt.id}
                                        className={cn(
                                            "flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors",
                                            checked ? "border-green-300 bg-green-50" : "border-border bg-background",
                                        )}
                                    >
                                        <CheckboxOption
                                            checked={checked}
                                            aria-label={`Mark option ${i + 1} as correct`}
                                            onCheckedChange={(val) => toggleAnswer(opt.id, val as boolean)}
                                        />
                                        <AutoGrowTextarea
                                            value={opt.text}
                                            onChange={(e) => updateOption(opt.id, e.target.value)}
                                            placeholder={`Option ${i + 1}`}
                                            className={OPTION_FIELD_CLASS}
                                        />
                                        {options.length > 2 && (
                                            <button
                                                type="button"
                                                aria-label={`Remove option ${i + 1}`}
                                                onClick={() => removeOption(opt.id)}
                                                className="text-muted-foreground hover:text-foreground focus-visible:ring-ring flex h-6 w-6 shrink-0 items-center justify-center rounded transition-colors focus:outline-none focus-visible:ring-2"
                                            >
                                                <X className="h-3.5 w-3.5" />
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        {options.length < 5 && (
                            <button
                                type="button"
                                onClick={addOption}
                                className="text-muted-foreground hover:text-foreground focus-visible:ring-ring flex items-center gap-1.5 self-start text-xs transition-colors focus:outline-none focus-visible:ring-2"
                            >
                                <Plus className="text-secondary h-3.5 w-3.5 font-semibold" />
                                Add option
                            </button>
                        )}
                    </div>
                )}

                {type === "single" && (
                    <div className="flex flex-col gap-2">
                        <span className="text-muted-foreground text-xs">{t("admin.lms.clickCorrectAnswer")}</span>
                        <div className="flex flex-col gap-2">
                            {options.map((opt, i) => {
                                const selected = answer === opt.id;
                                return (
                                    <div
                                        key={opt.id}
                                        className={cn(
                                            "flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors",
                                            selected ? "border-green-300 bg-green-50" : "border-border bg-background",
                                        )}
                                    >
                                        <button
                                            type="button"
                                            aria-label={`Mark option ${i + 1} as correct`}
                                            aria-pressed={selected}
                                            onClick={() => {
                                                const next = selected ? "" : opt.id;
                                                setAnswer(next);
                                                scheduleUpdate({
                                                    options: options.map((o) => ({
                                                        text: o.text,
                                                        isCorrect: o.id === next,
                                                    })),
                                                });
                                            }}
                                            className={cn(
                                                "focus-visible:ring-ring flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                                                selected
                                                    ? "border-transparent bg-green-600 text-white"
                                                    : "border-muted-foreground/30 hover:border-muted-foreground/50 text-transparent",
                                            )}
                                        >
                                            {selected && <Check className="h-4 w-4 stroke-3" />}
                                        </button>
                                        <AutoGrowTextarea
                                            value={opt.text}
                                            onChange={(e) => updateOption(opt.id, e.target.value)}
                                            placeholder={`Option ${i + 1}`}
                                            className={OPTION_FIELD_CLASS}
                                        />
                                        {options.length > 2 && (
                                            <button
                                                type="button"
                                                aria-label={`Remove option ${i + 1}`}
                                                onClick={() => removeOption(opt.id)}
                                                className="text-muted-foreground hover:text-foreground focus-visible:ring-ring flex h-6 w-6 shrink-0 items-center justify-center rounded transition-colors focus:outline-none focus-visible:ring-2"
                                            >
                                                <X className="h-3.5 w-3.5" />
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        {options.length < 5 && (
                            <button
                                type="button"
                                onClick={addOption}
                                className="text-muted-foreground hover:text-foreground focus-visible:ring-ring flex items-center gap-1.5 self-start text-xs transition-colors focus:outline-none focus-visible:ring-2"
                            >
                                <Plus className="text-secondary h-3.5 w-3.5 font-semibold" />
                                Add option
                            </button>
                        )}
                    </div>
                )}
                {type === "short" && (
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-row items-center">
                            <IconBadge variant={"secondary"} icon={<PencilLine />}>
                                {t("admin.lms.openResponse")}
                            </IconBadge>
                            <span className="text-muted-foreground ml-2 text-xs">
                                {t("admin.lms.responseNotGraded")}
                            </span>
                        </div>

                        <div className="bg-muted/40 rounded-lg border">
                            <div className="flex flex-row justify-between p-4">
                                <Mono className="text-muted-foreground text-xs uppercase">
                                    {t("admin.lms.whatLearnersSee")}
                                </Mono>
                                <Mono className="text-muted-foreground text-xs">{t("admin.lms.singleLine")}</Mono>
                            </div>
                            <div className="bg-border mx-4 mb-4 h-4 w-120 rounded-xl"></div>
                        </div>
                    </div>
                )}
                {type === "long" && (
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-row items-center">
                            <IconBadge variant={"secondary"} icon={<PencilLine />}>
                                {t("admin.lms.openResponse")}
                            </IconBadge>
                            <span className="text-muted-foreground ml-2 text-xs">
                                {t("admin.lms.responseNotGraded")}
                            </span>
                        </div>

                        <div className="bg-muted/40 rounded-lg border">
                            <div className="flex flex-row justify-between p-4">
                                <Mono className="text-muted-foreground text-xs uppercase">
                                    {t("admin.lms.whatLearnersSee")}
                                </Mono>
                                <Mono className="text-muted-foreground text-xs">{t("admin.lms.multiLine")}</Mono>
                            </div>
                            <div className="bg-border mx-4 mb-4 h-4 w-120 rounded-xl"></div>
                            <div className="bg-border mx-4 mb-4 h-4 w-120 rounded-xl"></div>
                            <div className="bg-border mx-4 mb-4 h-4 w-120 rounded-xl"></div>
                            <div className="bg-border mx-4 mb-4 h-4 w-80 rounded-xl"></div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
